import React, { useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { LOCAL_DRIVERS, EXPORT_COUNTRIES, getDepotCountries } from '../../data/localExport';
import { Globe2, UserPlus, Lock } from 'lucide-react';

/**
 * Recrutement local — 28/07/2026 (demande user) : déplacé de l'onglet RH
 * vers le Management des dépôts. Le vivier d'un pays n'est accessible QUE
 * si un dépôt y est physiquement ouvert (avant : simple activité export).
 * Embauche directe au salaire local (pas de négociation).
 */
export default function LocalRecruitSection() {
  const drivers = useGameStore(s => s.drivers) || [];
  const ownedDepots = useGameStore(s => s.ownedDepots) || [];
  const company = useGameStore(s => s.company);
  const firedDriversArchive = useGameStore(s => s.firedDriversArchive) || {};
  const hireDriver = useGameStore(s => s.hireDriver);

  const unlockedCountries = useMemo(
    () => getDepotCountries({ ownedDepots, company }),
    [ownedDepots, company],
  );

  const hiredDataIds = useMemo(() => new Set(drivers.map(d => d.dataId)), [drivers]);
  const available = LOCAL_DRIVERS.filter(
    d => unlockedCountries.has(d.localCountry) && !hiredDataIds.has(d.id),
  );

  const byCountry = useMemo(() => {
    const map = {};
    available.forEach(d => {
      (map[d.localCountry] = map[d.localCountry] || []).push(d);
    });
    return map;
  }, [available]);

  if (unlockedCountries.size === 0) {
    return (
      <div className="rounded-lg border border-dashed border-ink-600 bg-ink-800/40 p-5" data-testid="local-recruit-locked">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-200 mb-1">Recrutement local verrouillé</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Ouvrez un <span className="text-amber-300">dépôt à l'étranger</span> (carte
              stratégique, villes internationales) pour débloquer le vivier de chauffeurs
              locaux du pays concerné : profils recrutés au salaire de leur pays, qui
              ouvrent les <span className="text-cyan-300">contrats de cabotage</span> locaux.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {Object.entries(EXPORT_COUNTRIES).map(([code, c]) => (
                <span
                  key={code}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-ink-700/60 text-slate-400"
                  data-testid={`local-recruit-country-locked-${code}`}
                >
                  {c.flag} {c.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="local-recruit-section">
      <div className="flex items-center gap-2 mb-1">
        <Globe2 className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-200">Recrutement local</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Vos dépôts à l'étranger vous ouvrent les viviers locaux : chauffeurs recrutés
        au salaire de leur pays, spécialistes Export. Recruter un chauffeur local
        débloque les <span className="text-cyan-300">contrats de cabotage</span> de son pays
        sur le marché des contrats.
      </p>
      {available.length === 0 && (
        <p className="text-xs text-slate-500 italic" data-testid="local-recruit-empty">
          Tous les chauffeurs locaux de vos pays d'implantation sont déjà dans vos effectifs.
        </p>
      )}
      <div className="space-y-5">
        {Object.entries(byCountry).map(([country, list]) => (
          <div key={country}>
            <div className="text-xs font-semibold text-slate-300 mb-2">
              {EXPORT_COUNTRIES[country]?.flag} {EXPORT_COUNTRIES[country]?.name || country}
              <span className="ml-2 text-slate-500 font-normal">
                ({EXPORT_COUNTRIES[country]?.zone === 'intra' ? 'UE' : 'Hors UE'})
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {list.map(d => {
                const wasArchived = !!firedDriversArchive[d.id];
                return (
                  <div
                    key={d.id}
                    data-testid={`local-driver-card-${d.id}`}
                    className="rounded-lg border border-ink-600 bg-ink-800/60 p-3 flex flex-col gap-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-slate-100">{d.name}</div>
                        <div className="text-[11px] text-slate-400">
                          Niv.{d.level} · {d.specialty}{d.specialty2 ? ` + ${d.specialty2}` : ''} · Permis {d.licenses.join('/')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-emerald-400">{d.salaryGross.toLocaleString('fr-FR')} €/m</div>
                        <div className="text-[10px] text-slate-500">coût {d.employerCost.toLocaleString('fr-FR')} €</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-400">
                      <span className="text-emerald-300/90">+ {d.strength}</span>
                      <span className="text-amber-300/80 block">− {d.weakness}</span>
                    </div>
                    <button
                      data-testid={`hire-local-driver-${d.id}`}
                      onClick={() => hireDriver(d.id)}
                      className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      {wasArchived ? 'Ré-engager (local)' : 'Recruter (salaire local)'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
